import React from 'react';
import Modal from './Modal';
import Button from './Button';
import Icon from '../Icon';
import '../../styles/components/ConfirmDialog.css';

export interface ConfirmDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title?: string;
  description?: string;
  /** The record being acted on, shown by name so there is no doubt which one. */
  subject?: string;
  /** What will be lost or changed, one short line each (shown for destructive actions). */
  consequences?: string[];
  /** Plain-language reason the action failed. The dialog stays open so the person can read it. */
  error?: string | null;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: 'danger' | 'primary';
  loading?: boolean;
}

/**
 * Confirm an action. For a destructive one it names the record, lists what will
 * be lost, and, if the action fails, says why in plain words without closing.
 */
const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  open,
  onClose,
  onConfirm,
  title = 'Are you sure?',
  description,
  subject,
  consequences,
  error,
  confirmLabel = 'Delete',
  cancelLabel = 'Cancel',
  variant = 'danger',
  loading = false,
}) => (
  <Modal open={open} onClose={onClose} title={title} size="small">
    <div className={`cd-body cd-body--${variant}`}>
      {variant === 'danger' && (
        <span className="cd-icon" aria-hidden="true"><Icon name="alert-triangle" size={22} /></span>
      )}
      <div className="cd-content">
        {subject && <p className="cd-subject">{subject}</p>}
        {description && <p className="cd-description">{description}</p>}
        {consequences && consequences.length > 0 && (
          <ul className="cd-consequences" aria-label="What will be lost">
            {consequences.map(line => <li key={line}>{line}</li>)}
          </ul>
        )}
        {error && <p className="cd-error" role="alert">{error}</p>}
      </div>
    </div>
    <div className="cd-actions">
      <Button variant="ghost" size="sm" onClick={onClose} disabled={loading}>
        {cancelLabel}
      </Button>
      <Button variant={variant} size="sm" onClick={onConfirm} loading={loading}>
        {confirmLabel}
      </Button>
    </div>
  </Modal>
);

export default ConfirmDialog;
